import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  sumMoney
} from '@/components/WorkbookPage';
import { Scissors, Heart, AlertCircle } from 'lucide-react';

export const Rule4Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule4 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule4', { ...data, [field]: value });
  };
  
  const [cuts, setCuts] = useState(data.cutsToMake || [
    { item: '', amount: '', willCut: false },
    { item: '', amount: '', willCut: false },
    { item: '', amount: '', willCut: false },
    { item: '', amount: '', willCut: false },
    { item: '', amount: '', willCut: false },
  ]);
  
  const updateCut = (index, field, value) => {
    const newCuts = [...cuts];
    newCuts[index] = { ...newCuts[index], [field]: value };
    setCuts(newCuts);
    updateFormData('rule4', {
      ...data,
      cutsToMake: newCuts,
      totalSavings: sumMoney(newCuts, 'amount', (c) => c.willCut).toFixed(2)
    });
  };

  const totalSavings = sumMoney(cuts, 'amount', (c) => c.willCut);
  
  return (
    <WorkbookPage pageId="rule-4" prevPage="rule-3" nextPage="section-2-divider">
      <RuleHeader ruleNumber="4" title="Cut Spending Without Shame" section="stability" />
      
      {/* Overview */}
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Sometimes the math doesn't work. Income minus bills equals not enough. When that happens, 
        you need to find places to cut—but this doesn't mean you're a failure. It means you're 
        facing reality with courage.
      </Paragraph>
      <Paragraph>
        Cutting spending isn't punishment. It's a <strong>temporary strategy</strong> to create 
        breathing room. Once you're stable, you can add things back. But first, you need to get 
        on solid ground.
      </Paragraph>
      
      {/* Important mindset note */}
      <div className="my-8 bg-primary/5 border border-primary/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <Heart className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold mb-2">A Note on Shame</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Many of us carry shame about money. Maybe someone told us we were "bad with money" 
              or we've made choices we regret. Here's the truth: <strong>Your past doesn't define 
              your future.</strong> Every day is a chance to make a different choice. Cutting back 
              isn't shameful—it's wise. It's what responsible people do when times are tight.
            </p>
          </div>
        </div>
      </div>
      
      {/* Why It Matters */}
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        Small leaks sink big ships. That $15/month subscription you forgot about, the $5 daily 
        coffee habit, the "small" purchases that add up—they can mean the difference between 
        making rent and scrambling.
      </Paragraph>
      
      <Callout type="info">
        <strong>The goal isn't deprivation.</strong> It's alignment. You're making sure your 
        spending matches your values and priorities. Some things are worth cutting. Some aren't. 
        You get to decide.
      </Callout>
      
      {/* Categories to review */}
      <SectionTitle>Areas to Review</SectionTitle>
      
      <div className="grid gap-4 my-6">
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Subscriptions & Memberships</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Streaming services, gym memberships, apps, magazines, box subscriptions
          </p>
          <p className="text-xs bg-secondary px-2 py-1 rounded inline-block">
            Tip: Check bank statements for recurring charges you forgot about
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Food & Dining</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Eating out, delivery apps, coffee shops, convenience stores
          </p>
          <p className="text-xs bg-secondary px-2 py-1 rounded inline-block">
            Tip: Track every food purchase for one week—you might be surprised
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Transportation</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Gas, rideshare, car washes, parking
          </p>
          <p className="text-xs bg-secondary px-2 py-1 rounded inline-block">
            Tip: Can you combine trips? Carpool? Use public transit sometimes?
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Shopping Habits</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Impulse buys, sales you don't need, replacing things that work
          </p>
          <p className="text-xs bg-secondary px-2 py-1 rounded inline-block">
            Tip: Wait 48 hours before non-essential purchases
          </p>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <h4 className="font-semibold mb-2">Services You Could DIY</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Hair/nails (less often or at home), lawn care, cleaning, car washing
          </p>
          <p className="text-xs bg-secondary px-2 py-1 rounded inline-block">
            Tip: Not everything—your time has value too. Pick what makes sense.
          </p>
        </div>
      </div>
      
      {/* Examples */}
      <SectionTitle>Real-Life Examples</SectionTitle>
      
      <ExampleBox title="Jasmine's Cuts">
        <p>Jasmine needed to find $200/month. Here's what she identified:</p>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Netflix + Hulu + Disney+ → kept one, paused two</span>
            <span className="text-success font-medium">+$26</span>
          </div>
          <div className="flex justify-between">
            <span>Lunch out 3x/week → brought lunch from home</span>
            <span className="text-success font-medium">+$100</span>
          </div>
          <div className="flex justify-between">
            <span>Gym membership → switched to free YouTube workouts</span>
            <span className="text-success font-medium">+$45</span>
          </div>
          <div className="flex justify-between">
            <span>Salon every 4 weeks → every 8 weeks</span>
            <span className="text-success font-medium">+$40</span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex justify-between font-semibold">
            <span>Total Monthly Savings</span>
            <span className="text-success">$211</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Notice: She didn't cut everything. She kept what mattered most and trimmed the rest.
        </p>
      </ExampleBox>
      
      {/* What NOT to cut */}
      <div className="my-8 bg-destructive/5 border border-destructive/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-destructive shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold mb-2">What NOT to Cut</h3>
            <ul className="text-sm space-y-1">
              <li>• <strong>Health insurance</strong> (if you can help it—medical debt is crushing)</li>
              <li>• <strong>Necessary medications</strong></li>
              <li>• <strong>Basic nutritious food</strong> (cheap junk food costs more in health later)</li>
              <li>• <strong>Car insurance</strong> (required by law, protects you financially)</li>
              <li>• <strong>One thing that brings you joy</strong> (don't cut everything fun—you'll burn out)</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Step by Step */}
      <SectionTitle>Step-by-Step: Finding Your Cuts</SectionTitle>
      <StepList steps={[
        "Review your last 2-3 months of bank/card statements.",
        "Highlight every non-essential purchase.",
        "Group them by category (food, subscriptions, shopping, etc.).",
        "Total each category—which is the biggest leak?",
        "For each item, ask: 'Do I really need this? Does it align with my goals?'",
        "Make a list of specific cuts you're willing to make.",
        "Calculate your monthly savings.",
        "Redirect that money to bills, debt, or savings."
      ]} />
      
      {/* Worksheet */}
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">
          <Scissors className="h-5 w-5 inline mr-2" />
          Your Cutting Worksheet
        </h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-3">
              How much do you need to cut each month to make your budget work?
            </label>
            <input
              type="text"
              className="worksheet-input"
              placeholder="$___"
              value={data.monthlyTarget || ''}
              onChange={(e) => updateData('monthlyTarget', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-3">
              Items I'm considering cutting:
            </label>
            <div className="space-y-3">
              {cuts.map((cut, index) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={cut.willCut}
                    onChange={(e) => updateCut(index, 'willCut', e.target.checked)}
                    className="w-5 h-5 rounded border-border"
                  />
                  <input
                    type="text"
                    className="flex-1 border-b border-dashed border-border bg-transparent py-2 focus:outline-none focus:border-primary"
                    placeholder="Item/expense"
                    value={cut.item}
                    onChange={(e) => updateCut(index, 'item', e.target.value)}
                  />
                  <input
                    type="text"
                    className="w-24 border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none focus:border-primary"
                    placeholder="$/mo"
                    value={cut.amount}
                    onChange={(e) => updateCut(index, 'amount', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-success/10 rounded-lg p-4">
            <AutoTotal
              label="Total I will save by making these cuts:"
              value={totalSavings}
              hint="Only counts items you've checked to cut"
              testId="rule4-total-savings"
            />
          </div>
          
          <WorksheetInput
            label="One thing I'm keeping because it matters to me:"
            placeholder="What brings you joy that you're NOT cutting?"
            value={data.keepingItem || ''}
            onChange={(v) => updateData('keepingItem', v)}
          />
          
          <WorksheetInput
            label="Where will this saved money go?"
            placeholder="Bills? Debt? Savings? Emergency fund?"
            value={data.savedMoneyPlan || ''}
            onChange={(v) => updateData('savedMoneyPlan', v)}
            multiline
          />
        </div>
      </div>
      
      {/* Checklist */}
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem 
          checked={data.checklist1 || false}
          onChange={(v) => updateData('checklist1', v)}
        >
          I've reviewed my recent spending
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist2 || false}
          onChange={(v) => updateData('checklist2', v)}
        >
          I've identified specific items to cut
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist3 || false}
          onChange={(v) => updateData('checklist3', v)}
        >
          I know how much I'll save each month
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist4 || false}
          onChange={(v) => updateData('checklist4', v)}
        >
          I've kept at least one thing that brings me joy
        </CheckboxItem>
        <CheckboxItem 
          checked={data.checklist5 || false}
          onChange={(v) => updateData('checklist5', v)}
        >
          I have a plan for where the saved money will go
        </CheckboxItem>
      </div>
      
      {/* Recap */}
      <RecapBox items={[
        "Cutting spending is wise, not shameful",
        "Review statements to find hidden leaks",
        "Ask: 'Do I need this? Does it align with my goals?'",
        "Don't cut everything—keep one thing that brings joy",
        "Redirect savings to bills, debt, or emergency fund"
      ]} />
      
      {/* Affirmation */}
      <AffirmationBox>
        I am not defined by my past spending. Every day I choose to align my money with my values, 
        I move closer to the life I want.
      </AffirmationBox>
      
      {/* Optional Reflection */}
      <OptionalSection>
        <p className="mb-4">Reflect on these questions:</p>
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>What messages about spending did you learn growing up?</li>
          <li>Is there an emotion (boredom, stress, sadness) that triggers spending for you?</li>
          <li>What would it feel like to spend only on things that truly matter to you?</li>
        </ul>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
